import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cn } from '../cn.js';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Marks the field invalid and switches border/focus colour to `--danger`. Pair with `Field`'s
   *  `error` prop and set `aria-describedby` to the matching error id yourself — this component
   *  does not know the id. */
  invalid?: boolean;
}

/**
 * A multi-line text control — check-in notes, goal descriptions, the "Draft with AI" editable
 * result in `CheckInDialog`. Unlike `Input`, height is content-driven (`rows`) rather than pinned
 * to `--control-height`, since multi-line content is the point.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid = false, disabled, rows = 4, className, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      disabled={disabled}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(
        'w-full resize-y rounded-md border bg-surface px-3 py-2 text-sm text-fg',
        'placeholder:text-fg-subtle',
        'transition-colors duration-[var(--duration-fast)]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        invalid
          ? 'border-danger focus-visible:outline-danger'
          : 'border-border focus-visible:outline-accent',
        className,
      )}
      {...rest}
    />
  );
});
